import { MLSStaffMenuOptionsType } from 'components/SideMenu/MenuSection/types';
import { ESLockbox } from '@bb/domain/dist/types';
import React, { createContext, useContext } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import getLockboxTypeFilterOptions, {
  LockboxTypeFilterOptions,
} from '../../../api/AnsweringOrgController/getLockboxTypeFilterOptions';
import { useSearchParams } from '../AnsweringSearchPage/hooks';
import { isMenuType } from './types';

type MLSStaffContextType = {
  selectedMenu: MLSStaffMenuOptionsType;
  text: string;
  page: number;
  total: number;
  initialTotal: number;
  loading: boolean;
  mlsFilterOptions: { text: string; value: string }[];
  lockboxTypeFilterOptions: LockboxTypeFilterOptions;
  csvLink: string;
  selectedLockboxes: string[];
  selectedUsers: string[];
  setCsvLink: React.Dispatch<React.SetStateAction<string>>;
  setText: React.Dispatch<React.SetStateAction<string>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setTotal: React.Dispatch<React.SetStateAction<number>>;
  setInitialTotal: React.Dispatch<React.SetStateAction<number>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedLockboxes: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedUsers: React.Dispatch<React.SetStateAction<string[]>>;
  modalSearchText: string;
  setModalSearchText: React.Dispatch<React.SetStateAction<string>>;
  modalVisible: boolean;
  setModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  addUserPopoverVisible: boolean;
  setAddUserPopoverVisible: React.Dispatch<React.SetStateAction<boolean>>;
  manualCoopModalVisible: boolean;
  setManualCoopModalVisible: React.Dispatch<React.SetStateAction<boolean>>;
  manualCoopSerial: string;
  setManualCoopSerial: React.Dispatch<React.SetStateAction<string>>;
  manualCoopPin: string;
  setManualCoopPin: React.Dispatch<React.SetStateAction<string>>;
  lockboxPermissionsModalVisible: boolean;
  setLockboxPermissionsModalVisible: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  mlsLockboxes: ESLockbox[];
  setMlsLockboxes: React.Dispatch<React.SetStateAction<ESLockbox[]>>;
} | null;

const MLSStaffContext = createContext<MLSStaffContextType>(null);

export const useMLSStaffContext = () => {
  const mlsStaffContext = useContext(MLSStaffContext);

  if (!mlsStaffContext) {
    throw new Error(
      'useMLSStaffContext must be used within a MLSStaffContextProvider',
    );
  }

  return mlsStaffContext;
};

export const MLSStaffContextProvider: React.FC = ({ children }) => {
  const { page: initialPage } = useParams<{ page?: string }>();
  const history = useHistory();
  const location = useLocation();
  const [searchParam, setSearchParam] = useSearchParams<{
    search?: string;
  }>();
  const [text, setText] = React.useState(searchParam.search || '');
  const [page, setPage] = React.useState(parseInt(initialPage ?? '0', 10));
  const [mlsFilterOptions, setMlsFilterOptions] = React.useState<
    { text: string; value: string }[]
  >([]);
  const [lockboxTypeFilterOptions, setLockboxTypeFilterOptions] =
    React.useState<LockboxTypeFilterOptions>({
      masterLock: false,
      sentriLock: false,
      supra: false,
    });
  const [total, setTotal] = React.useState(0);
  const [initialTotal, setInitialTotal] = React.useState(0);
  const [csvLink, setCsvLink] = React.useState('');
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedLockboxes, setSelectedLockboxes] = React.useState([]);
  const [selectedUsers, setSelectedUsers] = React.useState([]);

  // Add User modal: Brokerage Search
  const [modalSearchText, setModalSearchText] = React.useState('');
  const [modalVisible, setModalVisible] = React.useState(false);
  const [addUserPopoverVisible, setAddUserPopoverVisible] =
    React.useState(false);
  const [manualCoopModalVisible, setManualCoopModalVisible] =
    React.useState(false);
  const [manualCoopSerial, setManualCoopSerial] = React.useState('');
  const [manualCoopPin, setManualCoopPin] = React.useState('');
  const [lockboxPermissionsModalVisible, setLockboxPermissionsModalVisible] =
    React.useState(false);
  const [mlsLockboxes, setMlsLockboxes] = React.useState<ESLockbox[]>([]);

  //  /#/mls/users => "users"
  const selectedMenu = location.pathname.split(
    '/',
  )[2] as MLSStaffMenuOptionsType;

  React.useEffect(() => {
    if (isMenuType(selectedMenu)) {
      switch (selectedMenu) {
        case 'users':
          history.replace({
            ...history.location,
            pathname: `/mls/users/${page}`,
          });
          break;
        case 'brokerages':
          history.replace({
            ...history.location,
            pathname: `/mls/brokerages/${page}`,
          });
          break;
        case 'lockboxes':
          history.replace({
            ...history.location,
            pathname: `/mls/lockboxes/${page}`,
          });
          break;
        default:
          break;
      }
    }
  }, [page]);

  React.useEffect(() => {
    (async () => {
      const fetchedLockboxTypeFilterOptions =
        await getLockboxTypeFilterOptions();
      setLockboxTypeFilterOptions(fetchedLockboxTypeFilterOptions);
    })();
  }, []);

  if (!isMenuType(selectedMenu)) return <>{children}</>;

  return (
    <MLSStaffContext.Provider
      value={{
        selectedMenu,
        text,
        mlsFilterOptions,
        page,
        total,
        initialTotal,
        setInitialTotal,
        setText,
        setPage,
        setTotal,
        csvLink,
        setCsvLink,
        loading,
        setLoading,
        selectedLockboxes,
        setSelectedLockboxes,
        selectedUsers,
        setSelectedUsers,
        lockboxTypeFilterOptions,
        modalSearchText,
        setModalSearchText,
        modalVisible,
        setModalVisible,
        addUserPopoverVisible,
        setAddUserPopoverVisible,
        manualCoopModalVisible,
        setManualCoopModalVisible,
        manualCoopSerial,
        setManualCoopSerial,
        manualCoopPin,
        setManualCoopPin,
        lockboxPermissionsModalVisible,
        setLockboxPermissionsModalVisible,
        mlsLockboxes,
        setMlsLockboxes,
      }}
    >
      {children}
    </MLSStaffContext.Provider>
  );
};

export default MLSStaffContext;
